// @ts-strict-ignore
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, Button, Typography, useToast } from "@aviary";
import { useSonicNPIImprovementsExperiment } from "@shared/hooks/useSonicNPIImprovementsExperiment/useSonicNPIImprovementsExperiment";
import { l } from "@shared/locales/i18n";
import type { FileWithPreview } from "@shared/types/fileWithPreview";

import { CheckmarkList } from "../CheckmarkList/CheckmarkList";
import { Dropzone } from "../Dropzone/Dropzone";

import { SUPPORTED_FILE_TYPES } from "./constants";

import * as styles from "./CredentialUpload.styles";

interface Props {
  uploadedFileHandler: (files: File[]) => void;
  isStudent?: boolean;
  capitalizedPatientsTerm?: string;
  treatmentPlanTerm?: string;
  isDisabled?: boolean;
  isEmerson?: boolean;
  uploadedFiles?: File[];
}

const CredentialUpload = ({
  uploadedFileHandler,
  isStudent,
  capitalizedPatientsTerm,
  treatmentPlanTerm,
  isDisabled,
  isEmerson,
  uploadedFiles,
}: Props) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();

  const [selectedFile, setSelectedFile] = useState<FileWithPreview>(null);
  const isSonicNPIImprovementsExperimentEnabled = useSonicNPIImprovementsExperiment();

  useEffect(() => () => URL.revokeObjectURL(selectedFile?.preview), [selectedFile]);

  const willReviewText = () => {
    if (isSonicNPIImprovementsExperimentEnabled) {
      return t(l.credentials.weWillReview);
    }

    if (isStudent) {
      return t(l.credentials.fullscriptWillReview.student);
    }
    if (isEmerson) {
      return t(l.credentials.fullscriptWillReview.emerson);
    }
    if (!isStudent && capitalizedPatientsTerm && treatmentPlanTerm) {
      return t(l.credentials.fullscriptWillReview.dynamicTerm, {
        capitalizedPatientsTerm,
        treatmentPlanTerm,
      });
    }

    return t(l.credentials.fullscriptWillReview.normal);
  };

  const dragAndDropText = () => {
    if (isStudent) return t(l.credentials.dragAndDropStudent);

    return t(l.credentials.dragAndDrop);
  };

  const renderCheckmarkListItems = () => {
    if (isStudent) {
      return (
        <CheckmarkList>
          <li css={[styles.listItem]}>
            <Typography>{t(l.credentials.ACopyofStudentID)}</Typography>
          </li>
        </CheckmarkList>
      );
    }

    return (
      <CheckmarkList>
        <li css={[styles.listItem]}>
          <Typography>{t(l.credentials.experiments.certUpAddressBack.license)}</Typography>
        </li>
        <li css={[styles.listItem]}>
          <Typography>{t(l.credentials.experiments.certUpAddressBack.otherFormats)}</Typography>
        </li>
      </CheckmarkList>
    );
  };

  const handleRemove = () => {
    setSelectedFile(null);
    uploadedFileHandler(null);
  };

  const handleUpload = (file: File | undefined) => {
    if (!file || !(file instanceof File)) {
      handleError();

      return;
    }

    try {
      setSelectedFile(
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );
      uploadedFileHandler([file]);
    } catch (error) {
      handleError();
    }
  };

  const handleError = () => {
    makeToast("error", t(l.credentials.rejectedFilesError));
  };

  return (
    <div css={styles.container}>
      {/*
        Hidden input for native form validation. This allows the browser to show
        validation messages when no files are uploaded, since file inputs can't
        be made required when using a custom dropzone interface.
      */}
      <input
        type="text"
        name="files"
        defaultValue={uploadedFiles?.length ? "hasFiles" : ""}
        required
        autoComplete="off"
        css={styles.hiddenInput}
        tabIndex={-1}
        ref={ref => {
          if (ref) {
            if (!uploadedFiles?.length) {
              ref.setCustomValidity(t(l.credentials.pleaseUploadCredentialsFile));
              ref.value = "";
            } else {
              ref.setCustomValidity("");
              ref.value = "hasFiles";
            }
          }
        }}
      />
      <Dropzone
        file={selectedFile}
        supportedFileTypes={SUPPORTED_FILE_TYPES}
        successMessage={{
          heading: t(l.credentials.continueToUpdate),
          description: willReviewText(),
        }}
        onUploadFile={handleUpload}
        onRemoveFile={handleRemove}
        previewUrl={selectedFile?.preview}
        onError={handleError}
      >
        <Arrange justify="center">
          <Typography isSecondaryWeight>{dragAndDropText()}</Typography>
        </Arrange>
        <div css={styles.checkMark}>{renderCheckmarkListItems()}</div>
        <Arrange justify="center">
          <Button
            intention="primary"
            disabled={isDisabled}
            isLoading={false}
            tabIndex={0}
            data-e2e="upload-credentials-button"
            data-testid="upload-credentials-button"
          >
            {isStudent ? t(l.credentials.Upload) : t(l.credentials.browseComputer)}
          </Button>
        </Arrange>
      </Dropzone>
    </div>
  );
};

export { CredentialUpload };
